import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { validateVerificationUrl } from '../utils/urlValidation';

const AuthVerify: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { verifyToken } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | null>(null);
  const [redirectDestination, setRedirectDestination] = useState<string>('/mentions');
  const hasVerified = useRef(false);

  useEffect(() => {
    const handleVerification = async () => {
      // Prevent double execution
      if (hasVerified.current) return;
      hasVerified.current = true;
      
      try {
        // Validate URL format first
        const urlValidation = validateVerificationUrl(location.pathname);
        if (!urlValidation.isValid) {
          setMessage(t('auth.malformedUrl', 'The verification link appears to be corrupted. Please request a new verification email.'));
          setMessageType('error');
          setIsLoading(false);
          return;
        }

        const token = searchParams.get('token');
        
        if (!token) {
          setMessage(t('auth.tokenExpired'));
          setMessageType('error');
          setIsLoading(false);
          return;
        }

        // Extract landingUrl and additional parameters from URL
        const landingUrl = searchParams.get('landingUrl') || undefined;
        const additionalParams: Record<string, string> = {};
        
        // Collect all URL parameters except 'token' and 'landingUrl' as additional params
        searchParams.forEach((value, key) => {
          if (key !== 'token' && key !== 'landingUrl') {
            additionalParams[key] = value;
          }
        });

        const result = await verifyToken(
          token, 
          landingUrl, 
          Object.keys(additionalParams).length > 0 ? additionalParams : undefined
        );
        
        if (result.success) {
          setMessage(result.message);
          setMessageType('success');
          
          // Determine redirect destination with language prefix
          const currentLanguage = i18n.language === 'en' ? 'en' : 'es';
          const mentionsRoute = t('routes.mentions');
          let redirectPath = `/${currentLanguage}${mentionsRoute}`; // default with language prefix
          
          if (result.landingUrl) {
            // If backend provides a landingUrl, ensure it has language prefix
            const landingUrl = result.landingUrl.startsWith('/') ? result.landingUrl : `/${result.landingUrl}`;
            redirectPath = landingUrl.startsWith(`/${currentLanguage}`) 
              ? landingUrl 
              : `/${currentLanguage}${landingUrl}`;
            
            // If there are additional parameters, append them to the redirect URL
            if (result.additionalParams && Object.keys(result.additionalParams).length > 0) {
              const params = new URLSearchParams(result.additionalParams);
              redirectPath += (redirectPath.includes('?') ? '&' : '?') + params.toString();
            }
          }
          
          setRedirectDestination(redirectPath);
          
          // Redirect after successful verification
          setTimeout(() => {
            navigate(redirectPath);
          }, 2000);
        } else {
          setMessage(result.message);
          setMessageType('error');
        }
      } catch (error) {
        setMessage(t('auth.loginError'));
        setMessageType('error');
      } finally {
        setIsLoading(false);
      }
    };

    handleVerification();
  }, [searchParams, verifyToken, navigate, t]);

  const handleReturnHome = () => {
    const currentLanguage = i18n.language === 'en' ? 'en' : 'es';
    const homeRoute = t('routes.home');
    navigate(`/${currentLanguage}${homeRoute}`);
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        textAlign: 'center' 
      }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('auth.authenticating')}
        </Typography>

        {isLoading ? (
          <Box sx={{ mt: 4 }}>
            <CircularProgress size={60} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              {t('auth.checkingAuth')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 4, width: '100%' }}>
            <Alert severity={messageType || 'info'} sx={{ mb: 3 }}>
              {message}
            </Alert>
            
            {messageType === 'error' && (
              <Button 
                variant="contained" 
                onClick={handleReturnHome}
                fullWidth
              >
                {t('nav.home')}
              </Button>
            )}
            
            {messageType === 'success' && (
              <Typography variant="body2" color="text.secondary">
                {redirectDestination.includes(t('routes.mentions'))
                  ? t('auth.redirectingToMentions', { title: t('mentions.title') })
                  : t('auth.redirecting')
                }
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default AuthVerify;